import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getProducts } from "../api";
import { formatPrice } from "../types";
import XButton from "./XButton";

const CartDrawer: React.FC = () => {
  const { items, isOpen, closeCart, removeItem, updateQty, subtotal } =
    useCart();
  const navigate = useNavigate();

  const [stockByProduct, setStockByProduct] = useState<
    Record<string, Record<string, number>>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    getProducts()
      .then((products) => {
        const map: Record<string, Record<string, number>> = {};
        for (const p of products) map[p.id] = p.sizes_json || {};
        setStockByProduct(map);
      })
      .catch(() => {});
  }, [isOpen]);

  const stockFor = (productId: string, size: string): number | null => {
    const sizes = stockByProduct[productId];
    if (!sizes) return null; // unknown yet — don't block the user
    return sizes[size] ?? 0;
  };

  // Once stock is known, silently clamp any item that now exceeds it (e.g.
  // someone else bought the last ones while this cart sat in localStorage).
  useEffect(() => {
    for (const item of items) {
      const stock = stockFor(item.productId, item.size);
      if (stock !== null && item.quantity > stock) {
        updateQty(item.productId, item.size, stock);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockByProduct, items]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[60] animate-fade-in"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-ks-surface z-[70] flex flex-col animate-slide-in-right shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-ks-border">
          <h2 className="font-saira uppercase tracking-widest text-xl text-ks-text">
            Your Cart
            {items.length > 0 && (
              <span className="ml-2 text-ks-secondary text-base">
                ({items.length})
              </span>
            )}
          </h2>
          <XButton onClick={closeCart} label="Close cart" />
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <ShoppingCart
                size={48}
                strokeWidth={1}
                className="text-ks-muted"
              />
              <p className="font-montserrat text-ks-secondary text-sm">
                Your cart is empty
              </p>
              <button
                onClick={closeCart}
                className="font-saira uppercase tracking-widest text-sm text-ks-accent hover:text-ks-text transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={`${item.productId}-${item.size}`}
                className="flex gap-4 py-4 border-b border-ks-border last:border-0"
              >
                {/* Image */}
                <div className="w-20 h-24 bg-ks-card flex-shrink-0 overflow-hidden">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-saira uppercase tracking-wide text-ks-text text-base leading-tight truncate">
                    {item.productName}
                  </p>
                  <p className="font-montserrat text-ks-secondary text-xs mt-1">
                    Size: {item.size}
                  </p>
                  <p className="font-montserrat text-ks-accent text-sm mt-1">
                    {formatPrice(item.unitPrice)}
                  </p>

                  {/* Qty controls */}
                  {(() => {
                    const stock = stockFor(item.productId, item.size);
                    const atMax = stock !== null && item.quantity >= stock;
                    return (
                      <>
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={() =>
                              updateQty(
                                item.productId,
                                item.size,
                                item.quantity - 1,
                              )
                            }
                            className="w-6 h-6 border border-ks-border text-ks-secondary hover:text-ks-text hover:border-ks-secondary flex items-center justify-center text-sm transition-colors"
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="font-montserrat text-ks-text text-sm w-4 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQty(
                                item.productId,
                                item.size,
                                stock !== null
                                  ? Math.min(item.quantity + 1, stock)
                                  : item.quantity + 1,
                              )
                            }
                            disabled={atMax}
                            className="w-6 h-6 border border-ks-border text-ks-secondary hover:text-ks-text hover:border-ks-secondary flex items-center justify-center text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-ks-secondary disabled:hover:border-ks-border"
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                          <button
                            onClick={() =>
                              removeItem(item.productId, item.size)
                            }
                            className="ml-auto text-ks-muted hover:text-ks-error transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                        {atMax && (
                          <p className="font-montserrat text-xs text-ks-muted mt-1.5">
                            Only {stock} in stock
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-6 border-t border-ks-border space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-montserrat text-ks-secondary text-sm">
                Subtotal
              </span>
              <span className="font-montserrat text-ks-text font-medium">
                {formatPrice(subtotal)}
              </span>
            </div>
            <p className="font-montserrat text-ks-muted text-xs">
              Shipping and discounts calculated at checkout
            </p>
            <button
              onClick={handleCheckout}
              className="w-full bg-ks-text text-ks-bg font-saira uppercase tracking-widest py-4 hover:bg-ks-warm transition-colors text-sm"
            >
              Checkout
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
